import React, { useMemo, useState, useRef, FC } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Box, lighten, useMediaQuery, useTheme } from "@mui/material";
import {
  CompletedExercisesWithAssetDto,
  UserExerciseDto,
} from "@growth-machine-llc/stridist-api-client";
import { RadarTooltip } from "./RadarTooltip";
import {
  BODYPART_BALANCE_LABELS,
  mapExercisesToRadarData,
  RadarDataItem,
} from "./utils";
import { useRadarResponsive } from "../coach-clients/hooks/useRadarResponsive";
import { colorSystem } from "../../theme";
import makeStyles from "@mui/styles/makeStyles";
import RadarBackground from "../../icons/RadarBackground";
import { PreviewType, usePreview } from "../../hooks/usePreview";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    width: "100%",
    height: 460,
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up("md")]: {
      height: 510,
      marginBottom: 0,
    },
    minHeight: 460,
  },

  polarGridLoading: {
    animation: "$polarWave 1.5s infinite ease-in-out",
  },
  "@keyframes polarWave": {
    "0%": { strokeOpacity: 0.2 },
    "50%": { strokeOpacity: 0.5 },
    "100%": { strokeOpacity: 0.2 },
  },
}));

interface IBodyPartBalanceRadarProps {
  exercises: CompletedExercisesWithAssetDto[];
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
  isLoading: boolean;
}

const ChartDot = (color: string, r: number, active: boolean) => {
  return ({ cx, cy, payload }: { cx: number; cy: number; payload: any }) => {
    return (
      (payload?.payload?.rawCount > 0 || active) && (
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill={color}
          stroke={colorSystem.white}
          strokeWidth={r / 2}
        />
      )
    );
  };
};

const BodyPartBalanceRadar: FC<IBodyPartBalanceRadarProps> = ({
  exercises,
  onExerciseSelect,
  isLoading,
}) => {
  const theme = useTheme();
  const s = useStyles();
  const isTouchMode = useMediaQuery("(pointer: coarse)");
  const [hoveredBodypart, setHoveredBodypart] = useState<RadarDataItem | null>(
    null,
  );
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [preview] = usePreview();
  const isMobilePreview = preview === PreviewType.MOBILE;

  const {
    outerRadius,
    fontSize: angleAxisFontSize,
    polarRadius,
    svgScale,
  } = useRadarResponsive();
  const adjustedPolarRadius = useMemo(() => {
    if (!polarRadius) return undefined;
    const factor = isMobilePreview ? 0.85 : 1;
    return polarRadius.map((r) => r * factor);
  }, [polarRadius, isMobilePreview]);

  const adjustedSvgScale = isMobilePreview ? svgScale * 0.85 : svgScale;
  const adjustedOuterRadius = isMobilePreview
    ? `calc(${outerRadius} * 0.95)`
    : outerRadius;

  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const radarData: RadarDataItem[] = useMemo(() => {
    if (isLoading) {
      return BODYPART_BALANCE_LABELS.map((subject) => ({
        subject,
        value: 0,
        fullMark: 100,
        rawCount: 0,
        relatedExercises: [],
        scaledValue: 0,
      }));
    }
    return mapExercisesToRadarData(exercises);
  }, [exercises, isLoading]);

  const closeTooltip = () => {
    setHoveredBodypart(null);
    setTooltipPos(null);
  };

  const handleRadarClick = (e: any) => {
    if (!isTouchMode) {
      setTooltipPos({ x: e.chartX, y: e.chartY });
    } else {
      if (!e?.activePayload?.length) {
        return;
      }

      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      let centerX = containerRect.width / 2;
      let centerY = containerRect.height / 2;

      if (tooltipRef.current) {
        const { offsetWidth: tooltipWidth, offsetHeight: tooltipHeight } =
          tooltipRef.current;
        // prettier-ignore
        centerX = Math.max(0, Math.min(centerX, window.innerWidth - tooltipWidth));
        // prettier-ignore
        centerY = Math.max(0, Math.min(centerY, window.innerHeight - tooltipHeight));
      }

      const selected = e?.activePayload?.[0]?.payload as RadarDataItem;

      if (!selected) return;

      const mappedSubject =
        BODYPART_BALANCE_LABELS.includes(selected.subject as any) &&
        (selected.subject as (typeof BODYPART_BALANCE_LABELS)[number]);

      if (!mappedSubject) return;

      setHoveredBodypart(selected);
      setTooltipPos({ x: centerX, y: centerY });
    }
  };

  const handleRadarMouseMove = (e: any) => {
    if (!e?.activePayload?.[0]) return;

    const hovered = e.activePayload[0].payload as RadarDataItem;
    if (!hovered) return;
    const mappedSubject =
      BODYPART_BALANCE_LABELS.includes(hovered.subject as any) &&
      (hovered.subject as (typeof BODYPART_BALANCE_LABELS)[number]);
    const index = BODYPART_BALANCE_LABELS.indexOf(mappedSubject);
    const angle = (360 / BODYPART_BALANCE_LABELS.length) * index;

    const radius = 220;
    const cx = 234 + radius * Math.cos((angle - 90) * (Math.PI / 180));
    const cy = 234 + radius * Math.sin((angle - 90) * (Math.PI / 180));

    setHoveredBodypart(hovered);
    if (!isTouchMode) {
      setTooltipPos({ x: cx, y: cy });
    }
  };

  const handleRadarMouseEnter = (e: any) => {
    const angleRad = (e.angle * Math.PI) / 180;
    const offset = 70;

    const x = e.cx + Math.cos(angleRad) * offset;
    const y = e.cy + Math.sin(angleRad) * offset;

    setTooltipPos({ x, y });
  };

  return (
    <Box ref={containerRef} className={s.root}>
      <RadarBackground
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: `translate(-50%, -50%) scale(${adjustedSvgScale})`,
          pointerEvents: "none",
        }}
        sides={BODYPART_BALANCE_LABELS.length}
        fill={lighten(theme.palette.primary.main, 0.9)}
      />
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart
          data={radarData}
          outerRadius={`calc(${adjustedOuterRadius} + 3%)`}
          margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
          onMouseEnter={isTouchMode ? undefined : handleRadarMouseEnter}
          onMouseMove={isTouchMode ? undefined : handleRadarMouseMove}
          onClick={handleRadarClick}
        >
          <Radar
            name="Muscle Activity Fill"
            dataKey="scaledValue"
            stroke="none"
            fill={theme.palette.primary.main}
            fillOpacity={1.0}
            strokeWidth={0}
            isAnimationActive={true}
            animationDuration={1200}
          />
          <PolarGrid
            gridType="circle"
            stroke={theme.palette.border.secondary}
            strokeWidth={1.5}
            fill="none"
            radialLines={true}
            strokeLinecap="round"
            polarRadius={adjustedPolarRadius}
            className={isLoading ? s.polarGridLoading : undefined}
          />

          {/* Render dots above polar grid */}
          {!isLoading && (
            <Radar
              name="dots"
              dataKey="scaledValue"
              stroke="none"
              fill="none"
              dot={ChartDot(theme.palette.primary.main, 3, false)}
              activeDot={ChartDot(theme.palette.primary.main, 4.5, true)}
            />
          )}
          <PolarAngleAxis
            dataKey="subject"
            tick={{
              fill: theme.palette.common.black,
              fontSize: isMobilePreview
                ? angleAxisFontSize * 0.9
                : angleAxisFontSize,
              fontWeight: 600,
            }}
          />
          <PolarRadiusAxis domain={[0, 110]} tick={false} axisLine={false} />

          {!isLoading && hoveredBodypart && (
            <Tooltip
              content={
                <RadarTooltip
                  selectedBodypart={hoveredBodypart}
                  tooltipPos={tooltipPos}
                  tooltipRef={tooltipRef}
                  radarData={radarData}
                  onExerciseSelect={onExerciseSelect}
                  isTouchMode={isTouchMode}
                  onClose={closeTooltip}
                />
              }
              cursor={false}
              isAnimationActive={false}
            />
          )}
        </RadarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BodyPartBalanceRadar;
